// src/context/ToastContext.tsx
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Toast, { ToastType } from '../components/Toast/Toast';

interface ToastItem {
  id: string;
  message?: ReactNode;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  showToast: (message: ReactNode, type?: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback(
    (message: ReactNode, type: ToastType = 'info', duration: number = 4000) => {
      const id = uuidv4();

      // Ensure we have a valid message
      const toastMessage = message || 'An error occurred';

      setToasts((prevToasts) => [
        ...prevToasts,
        {
          id,
          message: toastMessage,
          type,
          duration,
        },
      ]);
    },
    [],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
